import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
    getSharedAssignments,
    getSharedResponse,
    registerSharedDistributor,
    setSharedResponse,
    type SharedAssignment,
    type SharedRoundAnswer
} from "../api/assignments";
import {
    MATERIAL_OPTIONS,
    MATERIAL_OPTION_LABELS,
    normalizeMaterialOption,
    type MaterialOption
} from "../api/materialOption";
import {
    exportAssignmentAsA4Image,
    openAssignmentA4Image,
    prefersImageInNewTab
} from "./exportAssignmentImages";
import { AssignmentMapModal } from "./AssignmentMapModal";
import { searchAddress, type GeocodeSuggestion } from "../shared/geocode";
import { mapboxAccessToken } from "../map/config";
import "./shared-assignments-page.css";

/** Svarsläge per utdelare och omgång — samma tre lägen som admin ser i utdelningslistan. */
type AnswerStatus = "yes" | "no" | "none";

const answerStatus = (canDeliver?: boolean | null): AnswerStatus =>
    canDeliver === true ? "yes" : canDeliver === false ? "no" : "none";

const ANSWER_LABELS: Record<AnswerStatus, string> = {
    yes: "Ja",
    no: "Nej",
    none: "Inget svar"
};

/** Vem man är sparas lokalt, så man slipper välja namn varje gång länken öppnas. */
const STORAGE_KEY = "centerreach.shared.kretsMemberId";

const readStoredMemberId = (): string => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) ?? "";
    } catch {
        return "";
    }
};

const storeMemberId = (value: string): void => {
    try {
        if (value) {
            window.localStorage.setItem(STORAGE_KEY, value);
        } else {
            window.localStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // Blockerad storage — man får välja namn igen nästa gång.
    }
};

/** Serverns egen text vinner (t.ex. "namnet finns redan"), annars ett svar som går att agera på. */
const registrationErrorMessage = (error: unknown): string => {
    const response =
        !!error && typeof error === "object" && "response" in error
            ? (error as { response?: { status?: number; data?: { message?: unknown } } }).response
            : undefined;

    if (!response) {
        return "Ingen kontakt med servern. Kontrollera din uppkoppling och försök igen.";
    }
    if (typeof response.data?.message === "string" && response.data.message) {
        return response.data.message;
    }
    if (response.status === 400) {
        return "Kontrollera namn och adress och försök igen.";
    }
    return "Kunde inte lägga till dig. Försök igen senare.";
};

/** Omgångarna utdelaren sagt ja till, som en sorterad nyckel att jämföra osparade ändringar mot. */
const selectionKey = (roundIds: Iterable<string>): string => [...roundIds].sort().join("|");

/**
 * Öppen sida bakom en hemlig länk: de flesta utdelare saknar konto, så ingen inloggning krävs.
 * Token i URL:en är hela behörigheten — därför visas varken adresser, telefonnummer eller
 * andras kontaktuppgifter här. Man ser sitt eget svar, kretsens svarsläge och uppdragslistan.
 *
 * Flera utdelningar kan vara öppna samtidigt (t.ex. augusti och september). Utdelaren kryssar i
 * vilka hen kan vara med på och svarar en gång på hur materialet ska komma fram.
 */
export const SharedAssignmentsPage = (): JSX.Element => {
    const token = new URLSearchParams(window.location.search).get("t") ?? "";

    const { data, isLoading, isError, refetch: refetchShared } = useQuery({
        queryKey: ["assignments", "shared", token],
        queryFn: () => getSharedAssignments(token),
        enabled: !!token,
        staleTime: 60_000,
        retry: 1
    });

    const [memberId, setMemberId] = useState<string>(() => readStoredMemberId());

    // Ett sparat namn som inte finns kvar i kretsen ska inte låsa sidan.
    useEffect(() => {
        if (!data || !memberId) return;
        if (!data.distributors.some((d) => d.id === memberId)) {
            setMemberId("");
            storeMemberId("");
        }
    }, [data, memberId]);

    const rounds = useMemo(() => data?.rounds ?? [], [data?.rounds]);
    const hasRounds = rounds.length > 0;

    const responseQuery = useQuery({
        queryKey: ["assignments", "shared", token, "response", memberId],
        queryFn: () => getSharedResponse(token, memberId),
        enabled: !!token && !!memberId && hasRounds,
        staleTime: 30_000
    });

    // Vilka omgångar man kan vara med på, plus ett uttalat "ingen av dem" — utan det går inte
    // ett nej att skilja från ett formulär man inte fyllt i än.
    const [selectedRoundIds, setSelectedRoundIds] = useState<string[]>([]);
    const [declinedAll, setDeclinedAll] = useState(false);
    const [materialOption, setMaterialOption] = useState<MaterialOption | null>(null);

    const savedAnswers: SharedRoundAnswer[] = useMemo(
        () => responseQuery.data?.answers ?? [],
        [responseQuery.data?.answers]
    );

    const savedSelectedRoundIds = useMemo(
        () => savedAnswers.filter((answer) => answer.canDeliver === true).map((answer) => answer.roundId),
        [savedAnswers]
    );

    const hasSavedAnswer = savedAnswers.some((answer) => answer.canDeliver != null);
    const savedDeclinedAll = hasSavedAnswer && savedSelectedRoundIds.length === 0;
    const savedMaterialOption = normalizeMaterialOption(responseQuery.data?.materialOption);

    // Läggs en ny utdelning till efter att man svarat är svaret inte längre komplett — då ska
    // spara-knappen vara öppen igen, annars står det "Sparat ✓" fast en fråga är obesvarad.
    const hasUnansweredRound =
        hasSavedAnswer &&
        rounds.some(
            (round) => savedAnswers.find((answer) => answer.roundId === round.id)?.canDeliver == null
        );

    // Formuläret speglar det sparade svaret när man byter person.
    useEffect(() => {
        setSelectedRoundIds(savedSelectedRoundIds);
        setDeclinedAll(savedDeclinedAll);
        setMaterialOption(savedMaterialOption);
    }, [savedSelectedRoundIds, savedDeclinedAll, savedMaterialOption]);

    const toggleRound = (roundId: string) => {
        setDeclinedAll(false);
        setSelectedRoundIds((prev) =>
            prev.includes(roundId) ? prev.filter((id) => id !== roundId) : [...prev, roundId]
        );
    };

    const toggleDeclineAll = () => {
        setDeclinedAll((prev) => {
            const next = !prev;
            if (next) {
                // Ett nej på allt har inget materialval att bära på.
                setSelectedRoundIds([]);
                setMaterialOption(null);
            }
            return next;
        });
    };

    const saveMutation = useMutation({
        mutationFn: (input: { roundIds: string[]; materialOption: MaterialOption | null }) =>
            setSharedResponse({
                token,
                kretsMemberId: memberId,
                roundIds: input.roundIds,
                materialOption: input.materialOption
            }),
        onSuccess: () => {
            responseQuery.refetch();
            // Listan över kretsens svar visar även ens eget – den måste hämtas om.
            refetchShared();
            toast.success("Tack, ditt svar är sparat.");
        },
        onError: (error: unknown) => {
            // Skilj "nådde inte servern" från ett avvisat svar — utdelarna sitter ofta på mobilen.
            const reachedServer =
                !!error && typeof error === "object" && "response" in error &&
                !!(error as { response?: unknown }).response;
            toast.error(
                reachedServer
                    ? "Kunde inte spara svaret. Ladda om sidan och försök igen."
                    : "Ingen kontakt med servern. Kontrollera din uppkoppling och försök igen."
            );
        }
    });

    const isSaving = saveMutation.isPending;
    const hasSelection = selectedRoundIds.length > 0;
    const needsMaterialOption = hasSelection && !materialOption;
    const hasAnswered = hasSelection || declinedAll;

    const isDirty =
        hasUnansweredRound ||
        selectionKey(selectedRoundIds) !== selectionKey(savedSelectedRoundIds) ||
        declinedAll !== savedDeclinedAll ||
        materialOption !== savedMaterialOption;

    const handleSave = () => {
        if (!hasAnswered || needsMaterialOption) return;
        saveMutation.mutate({
            roundIds: selectedRoundIds,
            materialOption: hasSelection ? materialOption : null
        });
    };

    // Den som saknas i listan lägger till sig själv — annars fastnar hela svaret på att en
    // kretsansvarig hinner lägga in namnet först.
    const [showRegister, setShowRegister] = useState(false);
    const [registerName, setRegisterName] = useState("");
    const [registerPhone, setRegisterPhone] = useState("");
    const [addressQuery, setAddressQuery] = useState("");
    // Sätts bara när adressen valts ur förslagen — då följer koordinaterna med hemmapricken.
    const [pickedAddress, setPickedAddress] = useState<GeocodeSuggestion | null>(null);
    const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
    const addressDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const skipNextAddressSearchRef = useRef(false);

    useEffect(() => {
        if (skipNextAddressSearchRef.current) {
            skipNextAddressSearchRef.current = false;
            return;
        }
        if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
        if (!showRegister || addressQuery.trim().length < 3) {
            setSuggestions([]);
            return;
        }
        addressDebounceRef.current = setTimeout(async () => {
            setSuggestions(await searchAddress(addressQuery.trim(), mapboxAccessToken));
        }, 300);
        return () => {
            if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
        };
    }, [addressQuery, showRegister]);

    const closeRegister = () => {
        setShowRegister(false);
        setRegisterName("");
        setRegisterPhone("");
        setAddressQuery("");
        setPickedAddress(null);
        setSuggestions([]);
    };

    const selectSuggestion = (suggestion: GeocodeSuggestion) => {
        setPickedAddress(suggestion);
        skipNextAddressSearchRef.current = true;
        setAddressQuery(suggestion.place_name);
        setSuggestions([]);
    };

    const registerMutation = useMutation({
        mutationFn: (input: { name: string; address: string; picked: GeocodeSuggestion | null; phone: string }) =>
            registerSharedDistributor({
                token,
                name: input.name,
                homeAddress: input.address,
                homeLatitude: input.picked ? input.picked.center[1] : null,
                homeLongitude: input.picked ? input.picked.center[0] : null,
                phoneNumber: input.phone
            }),
        onSuccess: async (created) => {
            // Listan måste vara omhämtad innan namnet väljs, annars städar effekten ovan bort det.
            await refetchShared();
            setMemberId(created.id);
            storeMemberId(created.id);
            closeRegister();
            toast.success("Tack! Du är tillagd — svara på utdelningen nedan.");
        },
        onError: (error: unknown) => {
            toast.error(registrationErrorMessage(error));
        }
    });

    const isRegistering = registerMutation.isPending;
    const trimmedRegisterName = registerName.trim();
    const trimmedAddress = addressQuery.trim();
    const canRegister = trimmedRegisterName.length >= 2 && trimmedAddress.length >= 3 && !isRegistering;

    const handleRegister = (event: React.FormEvent) => {
        event.preventDefault();
        if (!canRegister) return;
        registerMutation.mutate({
            name: trimmedRegisterName,
            address: trimmedAddress,
            // Skrivs adressen om efter att förslaget valts hör koordinaterna inte längre ihop med den.
            picked: pickedAddress?.place_name === addressQuery ? pickedAddress : null,
            phone: registerPhone.trim()
        });
    };

    const [exportingId, setExportingId] = useState<string | null>(null);
    // Två lägen: titta på kartan (zoombar, bra i mobilen) eller spara ned den som bild.
    const [mapAssignment, setMapAssignment] = useState<SharedAssignment | null>(null);

    const handleDownloadMap = async (assignment: SharedAssignment) => {
        setExportingId(assignment.id);
        try {
            // Utan adress/telefon — den öppna sidan har inte de uppgifterna.
            // På telefonen öppnas bilden i en ny flik i stället för att laddas ned.
            const showImage = prefersImageInNewTab() ? openAssignmentA4Image : exportAssignmentAsA4Image;
            await showImage(assignment, { includeDistributorDetails: false });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Kunde inte hämta kartan.");
        } finally {
            setExportingId(null);
        }
    };

    const selectedName = useMemo(
        () => data?.distributors.find((d) => d.id === memberId)?.name ?? "",
        [data?.distributors, memberId]
    );

    const selectedRoundNames = rounds
        .filter((round) => selectedRoundIds.includes(round.id))
        .map((round) => round.name);

    /** Hur många som sagt ja/nej i en omgång — utdelarna vill se helheten. */
    const roundSummary = (roundId: string) => {
        const answers = (data?.distributors ?? []).map((distributor) =>
            distributor.answers.find((answer) => answer.roundId === roundId)?.canDeliver
        );
        return {
            yes: answers.filter((value) => value === true).length,
            no: answers.filter((value) => value === false).length,
            none: answers.filter((value) => value == null).length
        };
    };

    return (
        <div className="shared-assignments-page">
            <header className="shared-assignments-page__header">
                <span className="shared-assignments-page__logo">CenterReach</span>
            </header>

            <main className="shared-assignments-page__content">
                <h1 className="shared-assignments-page__title">
                    Utdelning{data?.municipalityName ? ` – ${data.municipalityName}` : ""}
                </h1>

                {!token && (
                    <p className="shared-assignments-page__hint">
                        Länken är ofullständig. Be den som skickade den om en ny länk.
                    </p>
                )}
                {token && isLoading && <p className="shared-assignments-page__hint">Laddar…</p>}
                {token && isError && (
                    <p role="alert" className="shared-assignments-page__error">
                        Länken fungerar inte längre. Be den som skickade den om en ny.
                    </p>
                )}

                {data && !hasRounds && (
                    <p className="shared-assignments-page__hint">
                        Ingen utdelning är öppen för svar just nu.
                    </p>
                )}

                {hasRounds && (
                    <section className="shared-answer">
                        <h2 className="shared-answer__heading">
                            {rounds.map((round) => round.name).join(" · ")}
                        </h2>

                        <label className="shared-answer__field">
                            Vem är du?
                            <select
                                value={memberId}
                                onChange={(event) => {
                                    setMemberId(event.target.value);
                                    storeMemberId(event.target.value);
                                }}
                            >
                                <option value="">Välj ditt namn</option>
                                {data?.distributors.map((distributor) => (
                                    <option key={distributor.id} value={distributor.id}>
                                        {distributor.name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {!memberId && !showRegister && (
                            <p className="shared-assignments-page__hint">Välj ditt namn för att svara.</p>
                        )}

                        {!memberId && !showRegister && (
                            <button
                                type="button"
                                className="shared-answer__link"
                                onClick={() => setShowRegister(true)}
                            >
                                Hittar du inte ditt namn? Lägg till dig här
                            </button>
                        )}

                        {showRegister && (
                            <form className="shared-register" onSubmit={handleRegister}>
                                <h3 className="shared-register__heading">Lägg till dig som utdelare</h3>
                                <p className="shared-assignments-page__hint">
                                    Adressen används för att göra ett utdelningsområde nära dig.
                                </p>

                                <label className="shared-answer__field">
                                    Namn
                                    <input
                                        type="text"
                                        value={registerName}
                                        autoComplete="name"
                                        maxLength={120}
                                        disabled={isRegistering}
                                        onChange={(event) => setRegisterName(event.target.value)}
                                    />
                                </label>

                                <label className="shared-answer__field shared-register__address">
                                    Adress
                                    <input
                                        type="text"
                                        value={addressQuery}
                                        autoComplete="street-address"
                                        maxLength={250}
                                        placeholder="Gatuadress, ort"
                                        disabled={isRegistering}
                                        onChange={(event) => {
                                            setAddressQuery(event.target.value);
                                            setPickedAddress(null);
                                        }}
                                    />
                                    {suggestions.length > 0 && (
                                        <ul className="shared-register__suggestions">
                                            {suggestions.map((suggestion) => (
                                                <li key={suggestion.id}>
                                                    <button
                                                        type="button"
                                                        className="shared-register__suggestion"
                                                        onClick={() => selectSuggestion(suggestion)}
                                                    >
                                                        {suggestion.place_name}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </label>

                                <label className="shared-answer__field">
                                    Telefon (valfritt)
                                    <input
                                        type="tel"
                                        value={registerPhone}
                                        autoComplete="tel"
                                        maxLength={40}
                                        disabled={isRegistering}
                                        onChange={(event) => setRegisterPhone(event.target.value)}
                                    />
                                </label>

                                <div className="shared-register__actions">
                                    <button
                                        type="submit"
                                        className="shared-answer__save"
                                        disabled={!canRegister}
                                    >
                                        {isRegistering ? "Lägger till…" : "Lägg till mig"}
                                    </button>
                                    <button
                                        type="button"
                                        className="shared-register__cancel"
                                        disabled={isRegistering}
                                        onClick={closeRegister}
                                    >
                                        Avbryt
                                    </button>
                                </div>
                            </form>
                        )}

                        {memberId && (
                            <>
                                <p className="shared-answer__question">
                                    Vilka utdelningar kan du vara med på?
                                </p>
                                <div className="shared-answer__options">
                                    {rounds.map((round) => (
                                        <label key={round.id} className="shared-answer__option">
                                            <input
                                                type="checkbox"
                                                checked={selectedRoundIds.includes(round.id)}
                                                disabled={isSaving}
                                                onChange={() => toggleRound(round.id)}
                                            />
                                            <span>{round.name}</span>
                                        </label>
                                    ))}
                                    <label className="shared-answer__option shared-answer__option--decline">
                                        <input
                                            type="checkbox"
                                            checked={declinedAll}
                                            disabled={isSaving}
                                            onChange={toggleDeclineAll}
                                        />
                                        <span>Jag kan inte vara med på någon av dem</span>
                                    </label>
                                </div>

                                {hasSelection && (
                                    <>
                                        <p className="shared-answer__question">Hur vill du få materialet?</p>
                                        <div className="shared-answer__options">
                                            {MATERIAL_OPTIONS.map((option) => (
                                                <label key={option} className="shared-answer__option">
                                                    <input
                                                        type="radio"
                                                        name="materialOption"
                                                        checked={materialOption === option}
                                                        disabled={isSaving}
                                                        onChange={() => setMaterialOption(option)}
                                                    />
                                                    <span>{MATERIAL_OPTION_LABELS[option]}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </>
                                )}

                                <button
                                    type="button"
                                    className="shared-answer__save"
                                    disabled={!hasAnswered || needsMaterialOption || isSaving || !isDirty}
                                    onClick={handleSave}
                                >
                                    {isSaving ? "Sparar…" : isDirty ? "Spara svar" : "Sparat ✓"}
                                </button>

                                {needsMaterialOption && (
                                    <p className="shared-assignments-page__hint">
                                        Välj hur du vill få materialet innan du sparar.
                                    </p>
                                )}
                                {hasUnansweredRound && !isSaving && (
                                    <p className="shared-answer__unsaved">
                                        En ny utdelning har lagts till – bekräfta ditt svar igen.
                                    </p>
                                )}
                                {isDirty && !hasUnansweredRound && hasAnswered && !needsMaterialOption && !isSaving && (
                                    <p className="shared-answer__unsaved">
                                        Du har ändringar som inte är sparade.
                                    </p>
                                )}
                                {!isDirty && hasSavedAnswer && responseQuery.data?.respondedAt && !isSaving && (
                                    <p className="shared-answer__receipt">
                                        Ditt svar är sparat:{" "}
                                        <strong>
                                            {selectedRoundNames.length > 0
                                                ? selectedRoundNames.join(" och ")
                                                : "kan inte vara med"}
                                        </strong>
                                        {materialOption
                                            ? `, ${MATERIAL_OPTION_LABELS[materialOption].toLowerCase()}`
                                            : ""}
                                        . Du kan ändra det här när som helst.
                                    </p>
                                )}
                            </>
                        )}
                    </section>
                )}

                {/* Vem mer som delar ut – utdelarna vill se helheten. */}
                {hasRounds && !!data?.distributors.length && (
                    <section className="shared-assignments-page__list">
                        <h2 className="shared-assignments-page__subtitle">Utdelare</h2>
                        <p className="shared-assignments-page__summary">
                            {rounds
                                .map((round) => {
                                    const summary = roundSummary(round.id);
                                    return `${round.name}: ${summary.yes} ja · ${summary.no} nej · ${summary.none} inget svar`;
                                })
                                .join(" — ")}
                        </p>

                        <div className="shared-assignments-table__scroll">
                            <table className="shared-assignments-table">
                                <thead>
                                    <tr>
                                        <th>Namn</th>
                                        {rounds.map((round) => (
                                            <th key={round.id}>{round.name}</th>
                                        ))}
                                        <th>Material</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.distributors.map((distributor) => {
                                        const isMe = distributor.id === memberId;
                                        const option = normalizeMaterialOption(distributor.materialOption);
                                        return (
                                            <tr
                                                key={distributor.id}
                                                className={isMe ? "shared-assignments-table__row--mine" : ""}
                                            >
                                                <td>
                                                    <span className="shared-assignments-table__name">
                                                        {distributor.name}
                                                        {isMe && (
                                                            <span className="shared-assignments-table__badge">
                                                                du
                                                            </span>
                                                        )}
                                                    </span>
                                                </td>
                                                {rounds.map((round) => {
                                                    const answer = distributor.answers.find(
                                                        (item) => item.roundId === round.id
                                                    );
                                                    const status = answerStatus(answer?.canDeliver);
                                                    return (
                                                        <td key={round.id}>
                                                            <span
                                                                className={`shared-assignments-table__chip shared-assignments-table__chip--${status}`}
                                                            >
                                                                {ANSWER_LABELS[status]}
                                                            </span>
                                                            {answer?.materialReceived && (
                                                                <span className="shared-assignments-table__material">
                                                                    material utlämnat
                                                                </span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td>{option ? MATERIAL_OPTION_LABELS[option] : "–"}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {!!data?.items.length && (
                    <section className="shared-assignments-page__list">
                        <h2 className="shared-assignments-page__subtitle">
                            Uppdrag i {data.municipalityName}
                        </h2>

                        <table className="shared-assignments-table">
                            <thead>
                                <tr>
                                    <th>Namn</th>
                                    <th className="shared-assignments-table__num">Antal</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((assignment) => {
                                    const isMine = !!memberId && assignment.kretsMemberId === memberId;
                                    return (
                                        <tr
                                            key={assignment.id}
                                            className={isMine ? "shared-assignments-table__row--mine" : ""}
                                        >
                                            <td>
                                                <span className="shared-assignments-table__name">
                                                    {assignment.name}
                                                    {isMine && (
                                                        <span className="shared-assignments-table__badge">ditt</span>
                                                    )}
                                                </span>
                                                {assignment.distributorName && (
                                                    <span className="shared-assignments-table__distributor">
                                                        {assignment.distributorName}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="shared-assignments-table__num">
                                                {assignment.targetAddressCount ?? "–"}
                                            </td>
                                            <td>
                                                {assignment.area ? (
                                                    <div className="shared-assignments-table__map-actions">
                                                        <button
                                                            type="button"
                                                            className="shared-assignments-table__map-button"
                                                            onClick={() => setMapAssignment(assignment)}
                                                        >
                                                            Visa karta
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="shared-assignments-table__map-button shared-assignments-table__map-button--secondary"
                                                            disabled={exportingId === assignment.id}
                                                            onClick={() => handleDownloadMap(assignment)}
                                                            title="Kartan som bild"
                                                        >
                                                            {exportingId === assignment.id
                                                                ? "Hämtar…"
                                                                : prefersImageInNewTab()
                                                                  ? "Visa bild"
                                                                  : "Spara bild"}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="shared-assignments-table__no-map">–</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>
                )}

                {selectedName && (
                    <p className="shared-assignments-page__footer-note">
                        Ditt svar sparas på <strong>{selectedName}</strong>. Är det inte du, byt namn i listan
                        ovan.
                    </p>
                )}
            </main>

            {mapAssignment && (
                <AssignmentMapModal
                    assignment={mapAssignment}
                    onClose={() => setMapAssignment(null)}
                    onDownload={() => handleDownloadMap(mapAssignment)}
                    isDownloading={exportingId === mapAssignment.id}
                />
            )}
        </div>
    );
};

export default SharedAssignmentsPage;
